import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { getClinicians } from "../../../api/services/api";

export type ClinicianOption = {
  id: number;
  users?: {
    fullName?: string;
    full_name?: string;
    email?: string;
    mobileNumber?: string;
    mobile_number?: string;
  };
  user?: {
    fullName?: string;
    full_name?: string;
    email?: string;
  };
  fullName?: string;
  full_name?: string;
  designation?: string;
};

const getClinicianName = (c: ClinicianOption) =>
  c?.users?.fullName ||
  c?.users?.full_name ||
  c?.user?.fullName ||
  c?.user?.full_name ||
  c?.fullName ||
  c?.full_name ||
  `Clinician #${c?.id}`;

const getClinicianEmail = (c: ClinicianOption) =>
  c?.users?.email || c?.user?.email || "";

const getClinicianPhone = (c: ClinicianOption) =>
  c?.users?.mobileNumber || c?.users?.mobile_number || "";

export const clinicianOptionLabel = (c: ClinicianOption) => {
  const name = getClinicianName(c);
  const email = getClinicianEmail(c);
  const phone = getClinicianPhone(c);
  const parts = [name];
  if (c.designation) parts.push(c.designation);
  if (email) parts.push(email);
  if (phone) parts.push(phone);
  parts.push(`ID ${c.id}`);
  return parts.join(" · ");
};

type Props = {
  value: ClinicianOption[];
  onChange: (value: ClinicianOption[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  helperText?: string;
  minSearchLength?: number;
  limit?: number;
};

/**
 * Debounced clinician typeahead against GET /admin/clinicians?search=
 * (name, email, mobile, clinician id on the server).
 */
export const ClinicianSearchAutocomplete = ({
  value,
  onChange,
  label = "Clinicians",
  placeholder = "Search by name, email, phone, or ID…",
  disabled,
  helperText,
  minSearchLength = 1,
  limit = 25,
}: Props) => {
  const [inputValue, setInputValue] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(inputValue.trim()), 300);
    return () => window.clearTimeout(t);
  }, [inputValue]);

  const enabled = debounced.length >= minSearchLength;

  const { data, isFetching } = useQuery(
    ["clinician-search", { search: debounced, limit }],
    getClinicians,
    {
      enabled,
      keepPreviousData: true,
    }
  );

  const options: ClinicianOption[] =
    data?.data?.clinicians || data?.data?.data || [];

  // Keep selected items in the options list so chips retain labels
  const mergedOptions = React.useMemo(() => {
    const byId = new Map<number, ClinicianOption>();
    for (const o of options) byId.set(o.id, o);
    for (const v of value) {
      if (!byId.has(v.id)) byId.set(v.id, v);
    }
    return Array.from(byId.values());
  }, [options, value]);

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      options={mergedOptions}
      value={value}
      filterOptions={(x) => x}
      getOptionLabel={(option) => clinicianOptionLabel(option)}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={isFetching}
      inputValue={inputValue}
      onInputChange={(_e, next, reason) => {
        if (reason === "input" || reason === "clear") setInputValue(next);
      }}
      onChange={(_e, next) => onChange(next)}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {getClinicianName(option)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {[getClinicianEmail(option), getClinicianPhone(option), `ID ${option.id}`]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          variant="standard"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

/** Hydrate selected clinicians by IDs for edit forms. */
export const useCliniciansByIds = (ids: number[]) => {
  const key = ids
    .slice()
    .sort((a, b) => a - b)
    .join(",");
  return useQuery(
    [
      "clinicians-by-ids",
      { ids: key, limit: String(Math.max(ids.length, 1)) },
    ],
    getClinicians,
    {
      enabled: ids.length > 0,
      select: (res: any): ClinicianOption[] => {
        const list =
          res?.data?.clinicians || res?.data?.data || res?.clinicians || [];
        return list.filter((c: ClinicianOption) => ids.includes(c.id));
      },
    }
  );
};
